import { FC, useState } from 'react'
import { ReactComponent as AnnouncementIcon } from 'src/assets/svg/announcement.svg'
import { ReactComponent as ResponseRateIcon } from 'src/assets/svg/response-rate.svg'
import { ReactComponent as ArrowUpIcon } from 'src/assets/svg/ri-arrow-up-circle-fill.svg'
import { SectionTitle } from 'src/components/SectionTitle'
import { Skeleton } from 'src/components/Skeleton'
import { TrendBadge } from 'src/components/TrendBadge'
import { useGetResponseTimeAnalysisQuery } from 'src/reactQuery/useGetResponseTimeAnalysisQuery'
import { ResponseRateChart, ResponseRateDatum } from './ResponseRateChart'
import classes from './responseTimeAnalysis.module.scss'

// From responseTimeAnalysis.module.scss: .statValue is 20px/100% and
// .responseRateCount is 12px, so the placeholders occupy the same line box the
// real numbers will and the 88px .statCard row does not reflow.
const STAT_VALUE_HEIGHT = 20
const RATE_COUNT_HEIGHT = 12

type ResponsePeriod = 'overall' | 'morning' | 'evening'

const PERIOD_FILTERS: { key: ResponsePeriod; label: string }[] = [
  { key: 'overall', label: 'Overall' },
  { key: 'morning', label: 'Response Time 09:00 - 19:00' },
  { key: 'evening', label: 'Response Time 19:00 - 09:00' },
]

interface PlatformResponseData {
  averageResponseTime: number
  id: string
  // Number of replies behind the average. Timestamps are minute-precision, so
  // a same-minute reply measures as 0 — this is what distinguishes "answered
  // instantly" from "never answered".
  messageCount?: number
  name: string // in minutes
}

interface OperatorResponseData {
  averageResponseTime: number
  id: string
  messageCount?: number
  name: string // in minutes
}

// Sub-minute averages would otherwise read as "0", which looks like missing
// data rather than an instant reply. Timestamps are minute-precision, so an
// average of exactly 0 only tells us "inside the same minute" — it can't be
// resolved to seconds.
const formatResponseTime = (minutes: number): string => {
  if (minutes <= 0) return 'under a minute'
  if (minutes < 1) return `${Math.round(minutes * 60)} sec`
  return `${minutes} min`
}

interface ResponseTimeAnalysisProps {
  data?: any
  fromDate?: string
  isLoading?: boolean
  pageId?: string
  toDate?: string
}

export const ResponseTimeAnalysis: FC<ResponseTimeAnalysisProps> = ({
  isLoading: externalLoading,
  pageId = '',
  fromDate,
  toDate,
}) => {
  const [activeTab, setActiveTab] = useState<'platform' | 'operator'>(
    'platform'
  )
  const [activePeriod, setActivePeriod] = useState<ResponsePeriod>('overall')
  // Operators view: switch the chart between response time and response rate.
  const [chartView, setChartView] = useState<'time' | 'rate'>('time')

  // Fetch data from API based on active tab
  const {
    data: apiData,
    isLoading: apiLoading,
    isFetching: apiFetching,
  } = useGetResponseTimeAnalysisQuery({
    fromDate,
    pageId,
    period: activePeriod,
    toDate,
    type: activeTab,
  })

  // First load only — there is genuinely nothing to show yet. keepPreviousData
  // keeps the previous snapshot on screen while switching tab/period, so a
  // refetch just dims the real values instead of flashing back to placeholders.
  const isInitialLoading = (externalLoading || apiLoading) && !apiData
  const isRefetching = apiFetching && !apiLoading

  const operatorAssignment = apiData?.operatorAssignment as
    | { enabled: boolean; pageName: string | null }
    | undefined
  const operatorAssignmentDisabled =
    activeTab === 'operator' && operatorAssignment?.enabled === false
  const operatorAssignmentPageName = operatorAssignment?.pageName ?? null

  // Use API data if available, otherwise use empty array
  const isApiData = apiData && apiData.data && apiData.stats
  const platformData = isApiData && activeTab === 'platform' ? apiData.data : []
  const operatorData = isApiData && activeTab === 'operator' ? apiData.data : []

  const currentData = activeTab === 'platform' ? platformData : operatorData

  // Per-operator answered / unanswered counts for the Response rate view.
  // Backend field pending; falls back to empty until it's wired up.
  const responseRateData: ResponseRateDatum[] =
    isApiData && Array.isArray((apiData as any).responseRateData)
      ? (apiData as any).responseRateData
      : []
  const showResponseRate = activeTab === 'operator' && chartView === 'rate'

  // Calculate stats based on current tab - use API stats if available
  const averageResponseTime = isApiData
    ? apiData.stats.averageResponseTime
    : currentData.length > 0
      ? Math.round(
          currentData.reduce(
            (sum: number, item: PlatformResponseData | OperatorResponseData) =>
              sum + item.averageResponseTime,
            0
          ) / currentData.length
        )
      : 0

  const fastestResponder = isApiData
    ? apiData.stats.fastestResponder
    : currentData.length > 0
      ? currentData.reduce(
          (
            prev: PlatformResponseData | OperatorResponseData,
            current: PlatformResponseData | OperatorResponseData
          ) =>
            current.averageResponseTime < prev.averageResponseTime
              ? current
              : prev
        )
      : null

  const slowestResponder = isApiData
    ? apiData.stats.slowestResponder
    : currentData.length > 0
      ? currentData.reduce(
          (
            prev: PlatformResponseData | OperatorResponseData,
            current: PlatformResponseData | OperatorResponseData
          ) =>
            current.averageResponseTime > prev.averageResponseTime
              ? current
              : prev
        )
      : null

  const slowestSameAsFastest =
    !!fastestResponder &&
    !!slowestResponder &&
    (fastestResponder.id === slowestResponder.id ||
      fastestResponder.name === slowestResponder.name)

  // Response rate — from API stats when present, otherwise a neutral
  // placeholder until the backend returns it.
  const responseRateStats = isApiData ? apiData.stats : null
  const responseRate =
    typeof responseRateStats?.responseRate === 'number'
      ? responseRateStats.responseRate
      : null
  const respondedCount =
    typeof responseRateStats?.responded === 'number'
      ? responseRateStats.responded
      : null
  const pendingCount =
    typeof responseRateStats?.pending === 'number'
      ? responseRateStats.pending
      : null

  // Calculate max value for chart scaling
  const maxResponseTime = Math.max(
    0,
    ...currentData.map(
      (item: PlatformResponseData | OperatorResponseData) =>
        item.averageResponseTime
    )
  )
  // Scale to the data (+20% headroom). A large fixed floor would squash every
  // bar into the baseline whenever the whole view is fast — e.g. the 09:00-19:00
  // window, where averages are seconds and the tallest is ~13 min.
  //
  // The axis is drawn as 5 equal steps, so the top of the scale must BE a whole
  // number of steps. Deriving the step first and rounding the ceiling up to it
  // keeps bars aligned with the gridlines they're measured against — otherwise
  // the axis read "20 Min" while bars were scaled against 17.
  // No headroom multiplier: padding the top by 20% and then rounding up to a
  // step boundary left the tallest bar at ~2/3 height with dead space above it.
  const step = Math.max(1, Math.ceil(Math.max(5, maxResponseTime) / 5))
  const chartMax = step * 5

  // Generate Y-axis labels (in hours and minutes)
  const yAxisLabels = []
  for (let i = 5; i >= 0; i--) {
    const minutes = step * i
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60)
      const remainingMinutes = minutes % 60
      yAxisLabels.push(
        remainingMinutes > 0
          ? `${hours} Hr ${remainingMinutes} Min`
          : `${hours} Hr`
      )
    } else {
      yAxisLabels.push(`${minutes} Min`)
    }
  }

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <div className={classes.titleSection}>
          <SectionTitle
            title="Response Time"
            tooltipContent="Analyze average response times across platforms and operators"
          />
        </div>
        <div className={classes.tabsContainer}>
          <button
            className={`${classes.tab} ${
              activeTab === 'platform' ? classes.active : ''
            }`}
            onClick={() => setActiveTab('platform')}
          >
            Platforms
          </button>
          <button
            className={`${classes.tab} ${
              activeTab === 'operator' ? classes.active : ''
            }`}
            onClick={() => setActiveTab('operator')}
          >
            Operators
          </button>
        </div>
      </div>

      <div className={classes.filtersRow}>
        <div className={classes.periodFilters}>
          {PERIOD_FILTERS.map(({ key, label }) => (
            <button
              className={`${classes.periodPill} ${
                activePeriod === key ? classes.periodPillActive : ''
              }`}
              key={key}
              onClick={() => setActivePeriod(key)}
              type="button"
            >
              {label}
            </button>
          ))}
        </div>

        {activeTab === 'operator' && (
          <div className={classes.viewToggle}>
            <button
              className={`${classes.viewToggleBtn} ${
                chartView === 'time' ? classes.viewToggleActive : ''
              }`}
              onClick={() => setChartView('time')}
              type="button"
            >
              Response time
            </button>
            <button
              className={`${classes.viewToggleBtn} ${
                chartView === 'rate' ? classes.viewToggleActive : ''
              }`}
              onClick={() => setChartView('rate')}
              type="button"
            >
              Response rate
            </button>
          </div>
        )}
      </div>

      {operatorAssignmentDisabled ? (
        <div className={classes.emptyState}>
          Operator response-time analytics requires conversation assignment to
          be enabled in Meta Business Suite. Once
          {operatorAssignmentPageName
            ? ` ${operatorAssignmentPageName}`
            : ' this page'}{' '}
          starts assigning conversations to specific admins, this view will
          populate automatically.
        </div>
      ) : (
        <div
          className={`${classes.content} ${
            isRefetching ? classes.contentFetching : ''
          }`}
        >
          <div className={classes.statsRow}>
            <div className={classes.statCard}>
              <div className={classes.statIcon}>
                <AnnouncementIcon />
              </div>
              <div className={classes.statContent}>
                <div className={classes.statLabel}>Average Response Time</div>
                <div className={classes.statValueRow}>
                  <div className={classes.statValue}>
                    {isInitialLoading ? (
                      <Skeleton height={STAT_VALUE_HEIGHT} width={104} />
                    ) : (
                      `${averageResponseTime} Minutes`
                    )}
                  </div>
                  {/* vs the previous period; a slower reply is the bad
                      direction, so a rise is drawn red. */}
                  {!isInitialLoading && (
                    <TrendBadge
                      lowerIsBetter
                      previousFromDate={
                        apiData?.stats?.averageResponseTimeComparison
                          ?.previousFromDate
                      }
                      previousToDate={
                        apiData?.stats?.averageResponseTimeComparison
                          ?.previousToDate
                      }
                      value={
                        apiData?.stats?.averageResponseTimeComparison
                          ?.changePercent
                      }
                    />
                  )}
                </div>
              </div>
            </div>

            <div className={classes.statCard}>
              <div className={classes.statIcon}>
                <ArrowUpIcon />
              </div>
              <div className={classes.statContent}>
                <div className={classes.statLabel}>Fastest Response</div>
                <div className={classes.statValue}>
                  {isInitialLoading ? (
                    <Skeleton height={STAT_VALUE_HEIGHT} width={88} />
                  ) : (
                    fastestResponder?.name || 'N/A'
                  )}
                </div>
              </div>
            </div>

            <div className={classes.statCard}>
              <div className={classes.statIcon}>
                <ArrowUpIcon style={{ transform: 'rotate(180deg)' }} />
              </div>
              <div className={classes.statContent}>
                <div className={classes.statLabel}>Slowest Response</div>
                <div className={classes.statValue}>
                  {isInitialLoading ? (
                    <Skeleton height={STAT_VALUE_HEIGHT} width={88} />
                  ) : slowestSameAsFastest ? (
                    '-'
                  ) : (
                    slowestResponder?.name || 'N/A'
                  )}
                </div>
              </div>
            </div>

            <div className={classes.statCard}>
              <div className={classes.statIcon}>
                <ResponseRateIcon />
              </div>
              <div className={classes.statContent}>
                <div className={classes.statLabel}>Response Rate</div>
                <div className={classes.responseRateRow}>
                  <div className={classes.statValue}>
                    {isInitialLoading ? (
                      <Skeleton height={STAT_VALUE_HEIGHT} width={48} />
                    ) : responseRate !== null ? (
                      `${responseRate}%`
                    ) : (
                      '-'
                    )}
                  </div>
                  <div className={classes.responseRateBreakdown}>
                    <div className={classes.responseRateItem}>
                      {isInitialLoading ? (
                        <Skeleton height={RATE_COUNT_HEIGHT} width={28} />
                      ) : (
                        <span className={classes.responseRateCount}>
                          {respondedCount !== null ? respondedCount : '-'}
                        </span>
                      )}
                      <span className={classes.responseRateText}>
                        Responded
                      </span>
                    </div>
                    <div className={classes.responseRateItem}>
                      {isInitialLoading ? (
                        <Skeleton height={RATE_COUNT_HEIGHT} width={28} />
                      ) : (
                        <span className={classes.responseRateCount}>
                          {pendingCount !== null ? pendingCount : '-'}
                        </span>
                      )}
                      <span className={classes.responseRateText}>Pending</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {showResponseRate ? (
            <ResponseRateChart data={responseRateData} />
          ) : (
            <div className={classes.chartContainer}>
              <div className={classes.chart}>
                <div className={classes.yAxis}>
                  {yAxisLabels.map((label, index) => (
                    <div className={classes.yLabel} key={index}>
                      {label}
                    </div>
                  ))}
                </div>

                <div className={classes.chartArea}>
                  <div className={classes.gridLines}>
                    <div className={classes.gridLine} />
                    <div className={classes.gridLine} />
                    <div className={classes.gridLine} />
                    <div className={classes.gridLine} />
                    <div className={classes.gridLine} />
                  </div>

                  <div className={classes.bars}>
                    {currentData.map(
                      (item: PlatformResponseData | OperatorResponseData) => {
                        // Calculate the actual bar height in pixels (max 260px)
                        const maxBarHeight = 260
                        const barHeightPixels =
                          chartMax > 0
                            ? (item.averageResponseTime / chartMax) *
                              maxBarHeight
                            : 0

                        // Draw a bar whenever the entry actually has replies —
                        // an average of 0 means "answered within the same
                        // minute", not "never answered", and keying off
                        // `> 0` hid the fastest responders entirely.
                        const hasResponses =
                          item.averageResponseTime > 0 ||
                          (item.messageCount ?? 0) > 0
                        // Keep the floor small: it exists so a 0-minute average
                        // (answered inside the same minute) still renders, not
                        // to pad every fast responder up to the same size. A
                        // 20px floor made 0.0 / 0.3 / 0.8 min bars identical.
                        const actualBarHeight = hasResponses
                          ? Math.max(barHeightPixels, 4)
                          : 0

                        return (
                          <div className={classes.barWrapper} key={item.id}>
                            <div
                              className={classes.bar}
                              style={{
                                backgroundColor: '#8080FF',
                                height: `${actualBarHeight}px`,
                              }}
                            >
                              <div className={classes.barTooltip}>
                                <div className={classes.tooltipTitle}>
                                  {item.name}
                                </div>
                                <div className={classes.tooltipContent}>
                                  <span className={classes.tooltipLabel}>
                                    Avg response
                                  </span>
                                  <span className={classes.tooltipValue}>
                                    {formatResponseTime(
                                      item.averageResponseTime
                                    )}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        )
                      }
                    )}
                  </div>

                  <div className={classes.xAxis}>
                    {currentData.map(
                      (item: PlatformResponseData | OperatorResponseData) => (
                        <div className={classes.xLabel} key={item.id}>
                          <span className={classes.itemName}>{item.name}</span>
                        </div>
                      )
                    )}
                  </div>
                </div>

                <div className={classes.yAxisRight}>
                  {yAxisLabels.map((label, index) => (
                    <div className={classes.yLabel} key={index}>
                      {label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
